import { io, Socket } from 'socket.io-client'
import { WhizMessageAtendimento, WhizMessageTipo } from '../mensagens/mensagens.service';
import { InboxType } from '../inbox/inbox.service';
import { ResponseContato, ResponseContatoImportacaoDto, ResponseContatoSincronizacaoDto } from '../contatos/contatos';
import { InfoMensagemGrupo, IsoTimestamp } from '../grupos/grupos.service';
import { Observable } from '@/utils/observer';
import { IdAtendimento, IdBot, IdCanal, IdConta, IdContato, IdGrupo, IdInbox, IdQuadro, IdUsuario } from '../common-types';
import { ResponseUsuario } from '../usuarios/usuarios.service';

export type WsEvent = 'connected'|'disconnected'|'connect_error'|'exception'| 'FORCE_LOGOUT' | 'RESYNC' | 'INBOX_PRONTO' | 'INBOX_BOT_IA_DESVINCULADO' | 'INBOX_ATUALIZADO' | 'USUARIO_INBOX_VINCULO_ALTERADO' | WppHistorySyncEvent | 'META_ONBOARDING_PROGRESSO'
/** Eventos do ciclo de sincronização do histórico na coexistência (req 2.3). */
export type WppHistorySyncEvent = 'WPP_HISTORY_SYNC_STARTED' | 'WPP_HISTORY_SYNC_PROGRESS' | 'WPP_HISTORY_SYNC_COMPLETE' | 'WPP_HISTORY_SYNC_FAILED' | 'WPP_HISTORY_SYNC_EXPIRED'
export type InboxEvent = 'MENSAGEM' | 'MENSAGEM.ID_EXTERNO' | 'MENSAGEM.SITUACAO' | 'MENSAGEM.ENQUETE_VOTO' | 'MENSAGEM.MIDIA' | 'MENSAGEM.FALHA_ENVIO' | 'CONEXAO' | 'DESCONEXAO' | 'QR_CODE' | 'PASSKEY_REQUIRED' | 'GRUPO_CRIADO' | 'GRUPO_ATUALIZADO' | 'GRUPO_REMOVIDO' | 'ATENDIMENTO_ATUALIZADO' | 'ATENDIMENTO_CRIADO' | 'ATENDIMENTO_INICIADO' | 'ATENDIMENTO_FINALIZADO' | 'NONE' | "USUARIO_PERMISSOES_ATUALIZADAS" | 'DISPARO_ATUALIZADO' | 'ANOTACAO_INTERNA_CRIADA' | 'ANOTACAO_INTERNA_EDITADA' | 'ANOTACAO_INTERNA_REMOVIDA'
export const InboxEventsArray = ['MENSAGEM','MENSAGEM.ID_EXTERNO','MENSAGEM.SITUACAO','MENSAGEM.ENQUETE_VOTO','MENSAGEM.MIDIA','CONEXAO','DESCONEXAO','QR_CODE','PASSKEY_REQUIRED','GRUPO_ATUALIZADO','GRUPO_CRIADO','GRUPO_REMOVIDO','ATENDIMENTO_ATUALIZADO','ATENDIMENTO_CRIADO','ATENDIMENTO_INICIADO','ATENDIMENTO_FINALIZADO','NONE','DISPARO_ATUALIZADO','ANOTACAO_INTERNA_CRIADA','ANOTACAO_INTERNA_EDITADA','ANOTACAO_INTERNA_REMOVIDA']
export const InboxEventsMap = new Map(InboxEventsArray.map(t => [t, true]))
export const PermissoesEventsArray = ['USUARIO_PERMISSOES_ATUALIZADAS'] as const;
export const PermissoesEventsMap = new Map(PermissoesEventsArray.map(t => [t, true]));


export type WebhookPayload = {
    timestamp: string
    tipo: string
}

/**
 * Payload dos eventos de sincronização do histórico na coexistência (req 2.3).
 * `dados` carrega ao menos o idInbox; `timestamp`/`tipo` podem não vir.
 */
export type WppHistorySyncPayload = WebhookPayload & {
    dados?: { idInbox?: string } & Record<string, unknown>
}

/**
 * Anotação interna criada/editada/removida.
 *
 * NÃO estende `WebhookPayload`: o gateway faz `emit(tipo, dados)`, então o que
 * chega no socket é o `ResponseAnotacaoInternaDto` puro — sem `timestamp` nem
 * `tipo` no topo. Declarar esses campos quebraria quem ligasse `supressIfLate`.
 *
 * O servidor publica no tópico do INBOX (não do chat), então o payload precisa
 * ser filtrado pelo alvo antes de entrar na timeline aberta.
 */
export type AnotacaoInternaPayload = {
    id:            string
    conteudo:      string
    idInbox:       IdInbox
    idUsuario:     string
    autor:         { id: string; nome: string }
    idContato:     string | null
    idAtendimento: string | null
    idGrupo:       string | null
    dataCriacao:   string
    dataEdicao:    string | null
    editada:       boolean
    del:           boolean
}

export type ConexaoPayload = WebhookPayload & {
    connected: true
    evento   : "connection.update"
    idInbox  : IdInbox
    inboxType: InboxType
    tipo     : "CONEXAO"
}

export type DesconexaoPayload = WebhookPayload & {
    connected: false
    evento   : "connection.update"
    idInbox  : IdInbox
    inboxType: InboxType
    tipo     : "DESCONEXAO"
    /**
     * Presente só quando a queda foi um DESVÍNCULO (alguém desconectou a conta
     * Meta, ou a Meta removeu nosso app do Business Manager) — não uma sessão que
     * caiu sozinha. Serve para não disparar o alerta "Inbox desconectado" por algo
     * que o próprio operador acabou de fazer e que a tela já vai mostrar mudado.
     *
     * Opcional porque os eventos vindos de webhook (Evolution/Z-API) não o trazem.
     */
    motivo?  : "DESVINCULO"
}

export type ConectandoPayload = WebhookPayload & {
    evento   : "connection.update"
    idInbox  : IdInbox
    inboxType: InboxType
    tipo     : "CONECTANDO"
}

export type QrcodePayload = WebhookPayload & {
    evento           : "qrcode.updated"
    idInbox          : IdInbox
    inboxType        : InboxType
    /** data-URI (data:image/png;base64,...) do QR recém-rotacionado */
    base64           : string
    codigoPareamento?: string | null
    tipo             : "QR_CODE"
}

export type PasskeyPayload = WebhookPayload & {
    evento          : "passkey.required"
    idInbox         : IdInbox
    inboxType       : InboxType
    codigoPareamento: string
    tipo            : "PASSKEY_REQUIRED"
}

export type ForceLogoutPayload = WebhookPayload & {
    usuarioId: IdUsuario,
    timestamp: number,
    motivo?: string
}

export type MensagemPayload = WebhookPayload & WhizMessageAtendimento & { idGrupo: IdGrupo|null }

/**
 * Evento por-usuário (subject NATS = idUsuario) emitido quando um inbox de
 * auto-onboarding fica pronto. O `dados` é o próprio inbox recém-criado — usado
 * pelo front para inseri-lo e selecioná-lo automaticamente. Campos de
 * WebhookPayload (tipo/timestamp) podem não vir; só os campos do inbox importam.
 */
export type InboxProntoPayload = WebhookPayload & {
    id       : IdInbox
    nome     : string
    idConta  : IdConta
    plataforma?: string
}

/**
 * O bot de IA excluiu a instância/agente que este inbox usava, e o servidor desfez
 * o vínculo. Publicado nos subjects do INBOX e da CONTA, para alcançar tanto quem
 * está com o inbox aberto quanto quem está em outro inbox da mesma conta.
 *
 * A área ADMIN não recebe este evento: ela não mantém socket (o `wsToken` é do
 * usuário). Lá o desvínculo aparece no próximo carregamento da lista de inboxes.
 *
 * **PLANO.** O servidor publica `{ tipo, dados }` no NATS, mas o socket-server
 * relaia `emit(tipo, dados)` — o que chega aqui é o conteúdo de `dados`, sem
 * envelope. Este tipo declarava o formato aninhado, e o handler que o seguia
 * (`payload.dados`) lia `undefined` e desistia: o evento chegava e nada
 * acontecia, sem erro nem log. Mesmo formato de `USUARIO_PERMISSOES_ATUALIZADAS`,
 * `CONEXAO`/`DESCONEXAO`, `INBOX_PRONTO` e `AnotacaoInternaPayload`.
 */
export type InboxBotIaDesvinculadoPayload = WebhookPayload & {
    idInbox          : IdInbox
    nomeInbox        : string
    idConta          : IdConta
    motivo           : 'INSTANCIA_EXCLUIDA' | 'AGENTE_EXCLUIDO'
    idInstanciaBotIa : string | null
    idAgenteBotIa    : string | null
    /** Nome do recurso excluído no bot-ia, quando ele o informou. */
    nomeRecurso      : string | null
}

/**
 * O vínculo do inbox com o bot de IA MUDOU (hoje: o próprio bot-ia vinculou a instância/agente
 * que acabou de criar). Contraparte de `INBOX_BOT_IA_DESVINCULADO`, no sentido oposto.
 *
 * O nome é genérico de propósito: o vínculo é só o primeiro motivo de o inbox mudar, e um evento
 * por motivo obrigaria este arquivo a crescer um nome a cada feature. O discriminador é `motivo`.
 *
 * **PLANO**, pela mesma razão de `InboxBotIaDesvinculadoPayload` — ver lá.
 */
export type InboxAtualizadoPayload = WebhookPayload & {
    idInbox          : IdInbox
    nomeInbox        : string
    idConta          : IdConta
    motivo           : 'VINCULO_BOT_IA'
    idInstanciaBotIa : string | null
    idAgenteBotIa    : string | null
    /** Estado ANTERIOR do vínculo, para a UI saber se foi substituição ou preenchimento. */
    anterior         : { idInstanciaBotIa: string | null, idAgenteBotIa: string | null }
    nomeRecurso      : string | null
    origem           : 'BOT_IA'
}

/**
 * O vínculo do usuário logado com um inbox mudou — um admin o vinculou ou o
 * desvinculou. Publicado no subject do USUÁRIO (não no do inbox): quem acabou de
 * ser vinculado ainda não está na sala do inbox, e quem foi desvinculado talvez
 * já tenha saído — nos dois casos o evento se perderia lá.
 *
 * O vínculo é o que decide o mapa `inboxes` do token E as permissões de escopo
 * inbox, então a reação certa é revalidar a sessão inteira, não remendar campo.
 *
 * **PLANO, como `AnotacaoInternaPayload`.** O servidor publica no NATS
 * `{ tipo, dados }`, mas o socket-server relaia `emit(tipo, dados)` — o que chega
 * aqui é o conteúdo de `dados`, sem envelope. Declarar `dados: {...}` aqui faria
 * o handler ler `payload.dados` (undefined) e desistir em silêncio: o evento
 * chega, e nada acontece. É o mesmo formato que
 * `USUARIO_PERMISSOES_ATUALIZADAS`, `CONEXAO`/`DESCONEXAO` e `INBOX_PRONTO` já
 * consomem — todos planos.
 */
export type UsuarioInboxVinculoAlteradoPayload = WebhookPayload & {
    idUsuario: IdUsuario
    idInbox  : IdInbox
    idConta  : IdConta | null
    nomeInbox: string | null
    acao     : 'VINCULADO' | 'DESVINCULADO'
}

export type InternalChatEvent =
    | 'CHAT_INTERNO.NOVA_MENSAGEM'
    | 'CHAT_INTERNO.MENSAGEM_EDITADA'
    | 'CHAT_INTERNO.CHAT_CRIADO'
    | 'CHAT_INTERNO.STATUS_USUARIO'
    | 'CHAT_INTERNO.MENSAGEM_LIDA'
    | 'CHAT_INTERNO.MENSAGEM_ENTREGUE'
    | 'CHAT_INTERNO.REACAO'
    | 'CHAT_INTERNO.DIGITANDO';

export type SincronizacaoContatosEvent = 'CONTATO_SINCRONIZACAO.ATUALIZADA';

export type ContatoImportacaoEvent = 'CONTATO_IMPORTACAO.ATUALIZADA' | 'CONTATO_IMPORTACAO.REMOVIDA';

/** Progresso/conclusão de exportação de conversas. Publicado só para quem pediu (subject idUsuario). */
export type ExportacaoConversasEvent = 'CONVERSA_EXPORTACAO.ATUALIZADA';

export type ContatoAtualizadoEvent = 'CONTATO_ATUALIZADO';

export type NotificacaoEvent = 'NOTIFICACAO.NOVA';

/**
 * Presença de digitação por conversa ("escrevendo..."), repassada pelo servidor a
 * partir do webhook `presence.update` do gateway WhatsApp. Deliberadamente FORA de
 * `InboxEvent`: `latestRecorded` é inicializado chave por chave e o dedup por
 * timestamp de `supressIfLate` não faz sentido para um estado efêmero.
 */
export type PresencaEvent = 'PRESENCA_ATUALIZADA';

/**
 * Estado de uma LIGAÇÃO de voz do WhatsApp (tocando / atendida / perdida /
 * recusada / encerrada), repassado do webhook `call` do gateway.
 *
 * FORA de `InboxEvent` pelo mesmo motivo de `PresencaEvent`: não tem chave em
 * `latestRecorded`, então o dedup por timestamp de `supressIfLate` leria NaN e
 * descartaria tudo.
 *
 * Só SINALIZAÇÃO viaja por aqui. O áudio vai em WebSocket direto browser ↔
 * gateway: NATS core é TCP e não descarta frame atrasado, e a sala por inbox
 * entregaria a voz do cliente a todo operador da inbox.
 */
export type LigacaoEvent = 'LIGACAO_ATUALIZADA';

/**
 * Pareamento em andamento: a Evolution avisa `connecting` no instante em que o
 * QR é lido, e o servidor publica isso NA HORA — o CONEXAO real ainda leva ~10s
 * (debounce anti-flicker de deploy no backend). Serve só para o modal do QR
 * mostrar "Conectando..." nessa janela cega.
 *
 * FORA de `InboxEvent` pelo mesmo motivo de `PresencaEvent`: é estado efêmero,
 * não tem chave em `latestRecorded` e o dedup por timestamp de `supressIfLate`
 * descartaria tudo (leitura daria NaN).
 */
export type ConexaoPendenteEvent = 'CONECTANDO';

/** Estados de chat presence que o WhatsApp emite. `paused` = parou de digitar. */
/**
 * Estados de digitação do CHAT INTERNO. Não tem `recording`: o chat entre
 * operadores não grava áudio. Deliberadamente separado de `PresencaChat` (WhatsApp)
 * — são transportes diferentes (socket direto vs NATS via whiz-server) e um ganhar
 * estado novo não deve arrastar o outro.
 */
export type PresencaChatInterno = 'composing' | 'paused';

export type PresencaChat = 'composing' | 'recording' | 'paused';

/**
 * `idAtendimento` e `idGrupo` são mutuamente exclusivos — mesma discriminação que
 * `MensagemPayload` usa. `codigo` é o telefone de quem está digitando (em grupo, o
 * participante; em 1:1, o próprio contato).
 */
export type PresencaAtualizadaPayload = WebhookPayload & {
    idInbox       : IdInbox
    idAtendimento?: IdAtendimento
    idGrupo      ?: IdGrupo
    idContato    ?: IdContato
    codigo        : string
    presenca      : PresencaChat
    /**
     * Só em grupo, e só quando o cache de participantes do servidor está quente.
     * Existe porque `chat.participants` no front só é carregado ao ABRIR o grupo:
     * sem isto, a linha da lista mostra "escrevendo..." sem nome. O servidor já
     * descarta o caso em que o "nome" é o número cru.
     */
    nome         ?: string | null
}

export type SincronizacaoContatosEventPayload = WebhookPayload & ResponseContatoSincronizacaoDto;

export type ContatoImportacaoEventPayload = WebhookPayload & ResponseContatoImportacaoDto;

export type ContatoAtualizadoEventPayload = WebhookPayload & ResponseContato & { updatedAt: string };

export type AtendimentoIniciadoPayload = WebhookPayload & {
    id             : IdAtendimento,
    idInbox        : IdInbox,
    idContato      : IdContato,
    idCanal        : IdCanal|null,
    dataCriacao    : string,
    dataFinalizacao: string|null,
    dataAbertura   : string,
    avaliacao      : number|null,
    idBot          : IdBot|null,
    idBotAcao      : string|null,
    contato        : ResponseContato
}

export type AtendimentoCriadoPayload = WebhookPayload & {
    id             : IdAtendimento,
    idInbox        : IdInbox,
    idContato      : IdContato,
    idCanal        : IdCanal|null,
    dataCriacao    : string,
    dataFinalizacao: string|null,
    dataAbertura   : string|null,
    avaliacao      : number|null,
    idBot          : IdBot|null,
    idBotAcao      : string|null,
    contato        : ResponseContato
    canal          : string|null
}

export type AtendimentoAtualizadoPayload = WebhookPayload & {
    id             : IdAtendimento,
    idInbox        : IdInbox
    idContato      : IdContato,
    idCanal        : IdCanal|null,
    idQuadro       : IdQuadro|null,
    naoLidas       : number|null,
    marcadoNaoLido?: boolean,
    dataCriacao    : string,
    dataFinalizacao: string|null,
    dataAbertura   : string|null,
    avaliacao      : number|null,
    idBot          : IdBot|null,
    idBotAcao      : string|null,

    contato: {
        id         : IdContato,
        nome       : string,
        codigo     : string,
        idConta    : IdConta,
        dataCriacao: string,
        tipo       : "WHATSAPP"|"FACEBOOK"|"INSTAGRAM"
    },

    canal             : string|null,
    ultimaInteracao   : IsoTimestamp|null,
    ultimaMensagem    : string|null,
    ultimoTipoMensagem: WhizMessageTipo|null,
    atendentes        : { id: string, nome: string, email: string }[]
}

export type GrupoCriadoPayload = WebhookPayload & {
    id                : IdGrupo,
    nome              : string,
    descricao         : string|null,
    imagem            : string|null,
    codigo            : string,
    idInbox           : IdInbox,
    ultimaMensagemData: string|null,
    ultimaMensagemInfo: InfoMensagemGrupo|null,
    ultimaLeitura     : string|null
    whitelistEnabled  ?: boolean
    whitelist         ?: ResponseUsuario[]

    /**
     * Campos de comunidade. Opcionais porque só `GRUPO_ATUALIZADO` os carrega:
     * `GRUPO_CRIADO` publica a linha recém-criada, e nesse instante o vínculo com a
     * comunidade ainda não foi resolvido (acontece em segundo plano, logo depois). O
     * grupo aparece sem identidade de comunidade e ganha uma no evento seguinte.
     */
    ehGrupoAvisos    ?: boolean
    somenteAdmins    ?: boolean
    codigoComunidade ?: string|null
    idComunidade     ?: string|null
    comunidade       ?: { id: string, nome: string, imagem: string|null }|null
}

export type GrupoRemovidoPayload = WebhookPayload & {
    id: string
}

export type AtualizacaoGrupoPayload = WebhookPayload & {
    evento   : "groups.update",
    inboxType: "WHATSAPP_EVOLUTION",
    autor    : string,
    subtipo  : "TITULO",
    timestamp: string,
    idGrupo  : "120363434052416842@g.us",
    idInbox  : string,
    data     : "grupo de teste de webhook e afins 🫦"
}

export type PermissoesEvent = 'USUARIO_PERMISSOES_ATUALIZADAS';

export type UsuarioPermissoesAtualizadasPayload = WebhookPayload & {
  tipo: 'USUARIO_PERMISSOES_ATUALIZADAS';
  idUsuario: string;
  idConta: string;
  idInbox: string | null;
  permissoesIds: string[];
};

export class WebsocketService {
    public ws: Socket;
    public latestRecorded: Record<InboxEvent, number>;

    public subscribed: Observable<boolean> = new Observable(false)
    // Bumps every time the socket re-establishes after having been up before
    // (i.e. a client-side blip), NOT on the first connect. Listeners resync on
    // it so messages missed mid-reconnect can't silently vanish. In-memory only
    // (resets on page reload), so a fresh load's first connect is never a "reconnect".
    public reconnected: Observable<number> = new Observable(0)
    private hasSubscribedBefore = false

    private getTokenFunc: () => Promise<{wsToken: string} | null>
    private lastBlur?: number;
    private lastFocus?: number;
    private callbackMap = new Map() as Map<string, AbortController>;
    private livenessTimer?: ReturnType<typeof setInterval>;

    private static storeTimestampCbAbtCtrl = new AbortController();
    private static blurEventCallbackAbtCtrl = new AbortController();
    private static instance: WebsocketService | null = null;

    static getWSS(getTokenFunc: () => Promise<{wsToken: string} | null>, wsUrl: string) {
        if (!this.instance)
            this.instance = new WebsocketService(wsUrl, getTokenFunc);
        return this.instance;
    }

    static clearWSS() {
        if (this.instance) this.instance = null;
    }

    private constructor(wsUrl: string, getTokenFunc: () => Promise<{wsToken: string} | null>) {
        // @ts-expect-error yup yup
        window.funny = this;

        this.getTokenFunc = getTokenFunc;
        this.latestRecorded = {
            NONE: 0,
            CONEXAO: 0,
            DESCONEXAO: 0,
            MENSAGEM: 0,
            QR_CODE: 0,
            GRUPO_CRIADO: 0,
            GRUPO_ATUALIZADO: 0,
            GRUPO_REMOVIDO: 0,
            ATENDIMENTO_ATUALIZADO: 0,
            ATENDIMENTO_CRIADO: 0,
            ATENDIMENTO_INICIADO: 0,
            ATENDIMENTO_FINALIZADO: 0,
            USUARIO_PERMISSOES_ATUALIZADAS: 0,
        };

        this.bindStoreTimestamp();

        // Native socket.io reconnection. Previously disabled in favour of a
        // hand-rolled loop that had no backoff (hammered every 300ms) and raced
        // itself via fixed delay(50) sleeps. The lib does retries with
        // exponential backoff + jitter; the token is refreshed on every
        // (re)connection attempt by the auth callback.
        this.ws = io(wsUrl, {
            autoConnect: false,
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 500,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5,
            transports: ['websocket'],
            auth: (cb) => {
                this.getTokenFunc()
                    .then((w) => cb(w ? { token: w.wsToken } : {}))
                    .catch(() => cb({}));
            },
        });
        this.registerLifecycleHandlers();

        // Debug
        console.debug('[WebsocketService] Initialized.')
        this.ws.onAny((...data) => (data.length == 2 && data.at(0) == 'MENSAGEM' && data.at(1)?.tipo == 'TEXTO' && /^mensagem\s\d$/.test(data.at(1)?.conteudo?.texto)) ? console.debug(`[✉️]`, data.at(1).conteudo.texto) : console.debug(`[WebsocketService]`, data)); // eslint-disable-line
    }

    bindStoreTimestamp() {
        const signal = WebsocketService.storeTimestampCbAbtCtrl.signal;
        document.addEventListener('focus', () => {setTimeout(() => {this.lastFocus = Date.now(); console.debug(`[FOCUS] 👁️ AT ${new Date(this.lastFocus).toLocaleTimeString()}`)}, 50)}, {signal});
        document.addEventListener('blur', () => {this.lastBlur = Date.now(); console.debug(`[BLUR] 💨 AT ${new Date(this.lastBlur).toLocaleTimeString()}`)}, {signal});
    }

    unbindStoreTimestamp() {
        WebsocketService.blurEventCallbackAbtCtrl.abort();
        WebsocketService.blurEventCallbackAbtCtrl = new AbortController();
        WebsocketService.storeTimestampCbAbtCtrl.abort();
        WebsocketService.storeTimestampCbAbtCtrl = new AbortController();
    }

    bindCallbackOnDocVisibilityRestored(callback: () => Promise<void>, minimumMsDifferenceForBlurEventToCallCallback: number = 0, clearPreviousBindings: boolean = false) {
        if (clearPreviousBindings) {
            WebsocketService.blurEventCallbackAbtCtrl.abort();
            WebsocketService.blurEventCallbackAbtCtrl = new AbortController();
        }
        document.addEventListener('focus', async () => { // eslint-disable-line
            if ((this.lastBlur ?? 0) - (this.lastFocus ?? 0) >= minimumMsDifferenceForBlurEventToCallCallback) {
                // Only reconnect if actually down — native reconnection keeps the
                // socket alive across short blurs, so forcing a teardown here just
                // created a needless gap. Then refetch to catch up.
                if (!this.ws.connected) await this.connect();
                void callback();
            }
        }, {signal: WebsocketService.blurEventCallbackAbtCtrl.signal});
        console.log('[WebsocketService] [BiCoVir] callback bound');
    }

    async connect() {
        this.subscribed.mutate(false);
        if (this.ws.connected) return;
        // The auth callback refreshes the token; the 'connect' lifecycle handler
        // emits 'conectar'. Native reconnection handles all retries from here.
        this.ws.connect();
    }

    private registerLifecycleHandlers() {
        // Every (re)connect: transport is up, tell the server who we are so it
        // (re)joins our rooms and resubscribes the NATS topics.
        this.ws.on('connect', () => {
            console.debug('[WebsocketService] transport connected → emitindo conectar');
            this.emit('conectar');
        });

        // Server acked our registration. First ack = initial connect; any later
        // ack means we recovered from a blip → bump `reconnected` so listeners
        // can resync what they may have missed while the socket was down.
        this.ws.on('conectado', () => {
            const isReconnect = this.hasSubscribedBefore;
            this.hasSubscribedBefore = true;
            this.subscribed.mutate(true);
            if (isReconnect) this.reconnected.mutate(this.reconnected.value + 1);
            // TESTE: liveness probe desativado p/ verificar se o ping-pong força
            // reconexões e gera o flood de CHAT_INTERNO.STATUS_USUARIO. Reativar
            // depois do teste. (Engine.IO já tem heartbeat nativo p/ socket morto.)
            // this.startLivenessProbe();
        });

        this.ws.on('disconnect', (reason: string) => {
            this.subscribed.mutate(false);
            this.stopLivenessProbe();
            console.warn('[WebsocketService] disconnect:', reason);
        });

        // Server-side WsException (e.g. guard rejection). Log only on purpose:
        // the receive path is unaffected by token expiry on a live socket, and
        // reconnecting here would risk the exact no-backoff loop we just removed.
        // Genuine auth failures are handled by the FORCE_LOGOUT / logout flow.
        this.ws.on('exception', (payload: unknown) => {
            console.warn('[WebsocketService] server exception:', payload);
        });

        this.ws.io.on('reconnect_attempt', (n: number) => console.debug('[WebsocketService] reconnect_attempt', n));
        this.ws.io.on('reconnect_error', (e: Error) => console.debug('[WebsocketService] reconnect_error', e?.message));
    }

    /**
     * Liveness probe: emits `ping`, awaits the server's `pong` ack. Returns
     * round-trip latency in ms, or null if the socket is down or no ack arrives
     * within timeoutMs. Catches a zombie socket (transport reports connected but
     * the server is unresponsive) — a state native reconnection alone misses,
     * since the lib only reacts to an actual transport drop.
     */
    async ping(timeoutMs: number = 3000): Promise<number | null> {
        if (!this.ws.connected) return null;

        const start = Date.now();
        try {
            await this.ws.timeout(timeoutMs).emitWithAck('ping');
            return Date.now() - start;
        } catch {
            // ack timed out: server did not pong within timeoutMs
            return null;
        }
    }

    /**
     * Periodic liveness probe, started on `conectado`, stopped on `disconnect`.
     * On a missed `pong` the socket is a zombie → drop it and reconnect. Native
     * reconnection does NOT auto-fire on a client-initiated disconnect, so we
     * call connect() explicitly. Idempotent: clears any previous probe first.
     */
    startLivenessProbe(intervalMs: number = 15000, timeoutMs: number = 3000) {
        this.stopLivenessProbe();
        this.livenessTimer = setInterval(async () => { // eslint-disable-line
            if (!this.ws.connected) return;
            const latency = await this.ping(timeoutMs);
            if (latency === null) {
                console.warn('[WebsocketService] [liveness] 🏓 no pong — forcing reconnect');
                this.stopLivenessProbe();
                this.ws.disconnect();
                void this.connect();
            } else {
                console.debug(`[WebsocketService] [liveness] 🏓 ${latency}ms`);
            }
        }, intervalMs);
        console.debug(`[WebsocketService] [liveness] probe started (every ${intervalMs}ms, timeout ${timeoutMs}ms)`);
    }

    stopLivenessProbe() {
        if (this.livenessTimer) {
            clearInterval(this.livenessTimer);
            this.livenessTimer = undefined;
        }
    }

    registerCallback<T extends WebhookPayload>(
        events: (InboxEvent | InternalChatEvent | WsEvent | 'conectado' | SincronizacaoContatosEvent | ContatoImportacaoEvent | ExportacaoConversasEvent | ContatoAtualizadoEvent | PresencaEvent | LigacaoEvent | ConexaoPendenteEvent)|(InboxEvent | InternalChatEvent | WsEvent | 'conectado' | SincronizacaoContatosEvent | ContatoImportacaoEvent | ExportacaoConversasEvent | ContatoAtualizadoEvent | PresencaEvent | LigacaoEvent | ConexaoPendenteEvent)[],
        cb: (_: T) => void | Promise<void>,
        supressIfLate: boolean = false,
        isExclusiveListener: boolean = false,
    ) {
        if (!Array.isArray(events)) events = [events];
        // console.log('[registerCallback] Callback registrado para ', events);

        for (const event of events) {

            if (isExclusiveListener)
                this.ws.removeAllListeners(event);

            // ==========================
            // CHAT INTERNO
            // ==========================
            if (event.startsWith('CHAT_INTERNO.') || event.startsWith('CONTATO_SINCRONIZACAO.') || event.startsWith('CONTATO_IMPORTACAO.') || event.startsWith('CONVERSA_EXPORTACAO.') || event.startsWith('NOTIFICACAO.')) {
                console.log('registrando evento: ', event);
                // continue, not return: returning here registered only the FIRST
                // event when an array was passed and silently dropped the rest.
                this.ws.on(event, cb);
                continue;
            }

            // ==========================
            // INBOX
            // ==========================
            if (!supressIfLate) {
                this.ws.on(event, cb);
                continue;
            }

            const inboxEvent = event as InboxEvent;

            this.ws.on(inboxEvent, (payload: T) => {

                const isConnectionEvent =
                    inboxEvent === 'CONEXAO' || inboxEvent === 'DESCONEXAO';

                const connectionCounterpart: 'CONEXAO' | 'DESCONEXAO' | 'NONE' =
                    inboxEvent === 'CONEXAO'
                        ? 'DESCONEXAO'
                        : inboxEvent === 'DESCONEXAO'
                        ? 'CONEXAO'
                        : 'NONE';

                const payloadTimestamp = Number(payload.timestamp);

                const lastEventTimestamp = this.latestRecorded[inboxEvent];
                const lastCounterpartTimestamp =
                    this.latestRecorded[connectionCounterpart] ?? 0;

                const isNewerEvent =
                    payloadTimestamp > lastEventTimestamp &&
                    payloadTimestamp > lastCounterpartTimestamp;

                if (isNewerEvent) {
                    this.latestRecorded[inboxEvent] = payloadTimestamp;

                    if (isConnectionEvent) {
                        this.latestRecorded[connectionCounterpart] = payloadTimestamp;
                    }

                    void cb(payload);
                }
            })
        }
    }

    emit(event: "conectar" | "desconectar", payload?: unknown) {
        this.ws.emit(event, payload);
    }

    async emitSystem(event: 'FORCE_LOGOUT', payload: {
        usuarioId: string
        motivo?: string
    }) {
        await this.connect();

        this.ws.emit(event, payload);
    }

    /**
     * "Digitando..." do chat interno. Único evento que o front ORIGINA em vez de
     * apenas consumir: o sinal é efêmero e não persiste, então não passa pelo
     * whiz-server — o socket-server relaia direto para a room do destinatário,
     * como já faz com `CHAT_INTERNO.STATUS_USUARIO`.
     *
     * Sem `idUsuario` no payload de propósito: quem digita é o dono do socket, e o
     * servidor carimba isso do JWT. Mandar daqui só criaria a ilusão de que o
     * cliente escolhe quem ele diz ser.
     *
     * NÃO usa `await this.connect()` como o `emitSystem`: presença é descartável e
     * de alta frequência; abrir conexão para anunciar digitação seria trabalho a
     * mais para um sinal que já nasceu velho. Socket fora do ar ⇒ o evento cai, o
     * indicador do outro lado expira pelo TTL.
     */
    emitDigitandoChatInterno(payload: {
        idChat: string
        idDestinatario: string
        presenca: PresencaChatInterno
    }) {
        if (!this.ws?.connected) return;
        this.ws.emit('CHAT_INTERNO.DIGITANDO', payload);
    }


    disconnect() {
        this.emit("desconectar");
        this.stopLivenessProbe();
        this.unbindStoreTimestamp();

        WebsocketService.clearWSS();

        return new Promise<void>((resolve) => {
            setTimeout(() => {
                this.ws.removeAllListeners();
                this.ws.disconnect();
                resolve();
            }, 150);
        });
    }
}
